import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import WarningIcon from '@mui/icons-material/Warning'

import colors from '@/assets/scss/_themes-vars.module.scss'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

export type DeleteConfirmationDialogProps = {
  /**
   * If `true`, show the loading indicator
   */
  isDeleting?: boolean
  maxWidth?: number
  /**
   * close dialog handler
   */
  onClose: () => void
  /**
   * delete handler
   */
  onDelete: () => void
  /**
   * If true, the component is shown.
   * @default false
   */
  open: boolean
  title: string
  message: string
}

export function DeleteConfirmationDialog(props: DeleteConfirmationDialogProps) {
  const { isDeleting = false, maxWidth, onClose, onDelete, open = false, title, message } = props
  const { t } = useTranslation()
  const isDeletingRef = useRef<boolean>(isDeleting)
  const isTooSmall = useIsMobile()
  const minWidth = isTooSmall ? '320px' : '500px'

  useEffect(() => {
    let prevIsDeleting = isDeletingRef.current
    let currentIsDeleting = isDeleting
    if (!currentIsDeleting && prevIsDeleting && open) {
      onClose()
    }
    isDeletingRef.current = currentIsDeleting
  }, [isDeleting, onClose, open])

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: [
          {
            minWidth: minWidth,
          },
          !!maxWidth && {
            maxWidth: `${maxWidth}px`,
          },
        ],
      }}
    >
      <DialogContent>
        <Stack alignItems="center" spacing={isTooSmall ? 1 : 2}>
          <Avatar sx={{ width: '60px', height: '60px', bgcolor: colors.red100 }}>
            <WarningIcon color="error" fontSize="large" />
          </Avatar>
          <Stack alignItems="center">
            <Typography variant="h6" component="div" sx={{ fontSize: '24px', textAlign: 'center' }}>
              {title}
            </Typography>
            <Typography variant="body1" component="div" sx={{ textAlign: 'center' }}>
              {message}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '0 24px 16px' }}>
        <Button variant="neutral" onClick={onClose}>
          {t('common:button.cancel.label')}
        </Button>
        <LoadingButton color="error" disableElevation loading={isDeleting} variant="contained" onClick={onDelete} data-testid="delete-confirmation-button">
          {t('common:button.confirm.label')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default DeleteConfirmationDialog
